/**
 * ChatFloatingButton
 * Fixed bottom-right FAB that opens the AI Chat panel.
 * Elite+ users: opens chat. Lower tiers: shows upgrade modal.
 * Shows unread badge with pulse animation for Elite+ users.
 */

import UpgradeModal from '@components/ui/UpgradeModal';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useChatStore, useChatUnreadCount } from '@stores/chatStore';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { AnimatePresence, motion } from 'framer-motion';
import { Lock, MessageSquare } from 'lucide-react';
import { type ReactElement, useState } from 'react';

export function ChatFloatingButton(): ReactElement | null {
  const { hasAccess, isHidden } = useFeatureAccess();
  const unreadCount = useChatUnreadCount();
  const isOpen = useChatStore((s) => s.isOpen);
  const isPicksConfigFullscreen = useGlobalShortcutsStore((s) => s.isPicksConfigFullscreen);
  const isBuilderExpanded = useParlayBuilderStore((s) => s.isExpanded);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Don't render at all if feature is hidden for this tier
  if (isHidden('ai_chat')) return null;

  // Hide when picks config is expanded fullscreen or parlay builder is expanded
  if (isPicksConfigFullscreen || isBuilderExpanded) return null;

  const canChat = hasAccess('ai_chat');

  // Hide when chat panel is open (Elite+ only)
  if (canChat && isOpen) return null;

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-floating-button"
        data-can-chat={canChat ? 'true' : 'false'}
        onClick={() => {
          if (canChat) {
            useChatStore.getState().toggle();
          } else {
            setShowUpgrade(true);
          }
        }}
        aria-label={canChat ? 'Open AI Chat (Cmd+K)' : 'AI Chat (Elite feature)'}
        className="fixed bottom-[6.5rem] right-6 z-40 w-12 h-12 rounded-full bg-theme-primary-fill text-theme-on-primary-fill shadow-lg shadow-theme-primary/30 flex items-center justify-center hover:bg-theme-primary-fill/90 transition-colors"
      >
        <MessageSquare className="w-5 h-5" />

        {/* Lock icon overlay for non-Elite users */}
        {!canChat && (
          <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-theme-surface border border-theme-border rounded-full flex items-center justify-center">
            <Lock className="w-3 h-3 text-theme-warning" />
          </span>
        )}

        {/* Unread badge for Elite+ users */}
        <AnimatePresence>
          {canChat && unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-theme-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Upgrade modal for non-Elite users */}
      {showUpgrade && (
        <UpgradeModal mode="feature" feature="ai_chat" onDismiss={() => setShowUpgrade(false)} />
      )}
    </>
  );
}
